import { ASSET_BUCKETS, ASSET_UPLOAD_CONFIG } from '~/utils/constants';
import { uploadBlobResumable, startResumableSession } from './resumableUpload';
import type { AssetUploadOptions, AssetUploadResult, AssetType, AssetStatusResponse } from '~/types/asset';
import * as Sentry from '@sentry/remix';
import { userIdStore } from '~/lib/stores/user';
import { addPendingAssetJobId } from '~/lib/stores/assetUploadPendings';

/**
 * Generate a unique job ID for asset upload
 */
function generateJobId(assetType: AssetType): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${assetType}-${timestamp}-${random}`;
}

/**
 * Determine bucket based on asset type
 */
function getBucketForAssetType(assetType: AssetType): string {
  return assetType === 'image' ? ASSET_BUCKETS.IMAGES_RAW : ASSET_BUCKETS.VIDEOS_RAW;
}

/**
 * Get UTF-8 byte length of a string (browser-compatible)
 */
function getUtf8ByteLength(str: string): number {
  return new TextEncoder().encode(str).length;
}

/**
 * Truncate string to fit within UTF-8 byte limit
 */
function truncateToByteLength(str: string, maxBytes: number): string {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder('utf-8', { fatal: false });
  const bytes = encoder.encode(str);

  if (bytes.length <= maxBytes) {
    return str;
  }

  // Truncate and decode, removing any incomplete characters
  const truncated = bytes.slice(0, maxBytes);
  let decoded = decoder.decode(truncated);

  // Remove replacement characters from incomplete UTF-8 sequences
  decoded = decoded.replace(/\uFFFD/g, '');

  return decoded;
}

/**
 * Sanitize filename according to GCS object name requirements
 * - Removes/replaces special characters with underscore
 * - Removes carriage return and line feed characters
 * - Avoids reserved names and problematic characters
 */
function sanitizeFileName(fileName: string): string {
  // Remove extension for processing
  const lastDotIndex = fileName.lastIndexOf('.');
  const nameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';

  // Remove carriage return and line feed characters (mandatory requirement)
  let sanitized = nameWithoutExt.replace(/[\r\n]/g, '');

  // Replace problematic characters with underscore (strong recommendations)
  // Avoid: #, [, ], *, ?, :, ", <, >, |, and control characters
  sanitized = sanitized.replace(/[#\[\]*?:"<>|]/g, '_');

  // Replace other special characters and spaces with underscore
  sanitized = sanitized.replace(/[^\w\-._]/g, '_');

  // Remove consecutive underscores
  sanitized = sanitized.replace(/_+/g, '_');

  // Remove leading/trailing underscores and dots
  sanitized = sanitized.replace(/^[._]+|[._]+$/g, '');

  // Ensure it's not a reserved name
  if (sanitized === '.' || sanitized === '..' || sanitized.startsWith('.well-known/acme-challenge/')) {
    sanitized = `file_${sanitized}`;
  }

  // Ensure minimum length (at least 1 character)
  if (sanitized.length === 0) {
    sanitized = 'file';
  }

  // Truncate to ensure total length (with jobId and extension) doesn't exceed 1024 bytes
  // We'll leave room for: sanitizedname-jobid.extension (estimate: 100 chars for jobId + extension)
  const maxNameLength = 900; // Conservative limit to account for UTF-8 encoding

  if (getUtf8ByteLength(sanitized) > maxNameLength) {
    sanitized = truncateToByteLength(sanitized, maxNameLength);
  }

  return sanitized + extension;
}

/**
 * Extract jobId from object name
 * Format: sanitizedname-jobid.extension
 */
export function extractJobIdFromObjectName(objectName: string): string | null {
  const match = objectName.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
  return match ? match[0] : null;
}

/**
 * Generate object name for asset
 * Format: sanitizedname-jobid.extension
 * This ensures jobId is retrievable from the final object name
 */
function generateObjectName(jobId: string, assetType: AssetType, originalName?: string): string {
  // Get extension
  const extension =
    assetType === 'image'
      ? originalName?.match(/\.(jpg|jpeg|png|webp|avif)$/i)?.[0] || '.png'
      : originalName?.match(/\.(mp4|webm|mov|avi)$/i)?.[0] || '.mp4';

  // Sanitize original name if provided
  let sanitizedName = 'asset';

  if (originalName) {
    const sanitized = sanitizeFileName(originalName);

    // Remove extension from sanitized name (we'll add it back with jobId)
    const nameWithoutExt = sanitized.replace(/\.[^.]+$/, '');

    sanitizedName = nameWithoutExt || 'asset';
  }

  // Format: sanitizedname-jobid.extension
  const objectName = `${sanitizedName}-${jobId}${extension}`;

  // Final validation: ensure total length doesn't exceed 1024 bytes (mandatory requirement)
  const encodedLength = getUtf8ByteLength(objectName);

  if (encodedLength > 1024) {
    // Truncate sanitizedName to fit within limit
    const jobIdAndExtLength = getUtf8ByteLength(`-${jobId}${extension}`);
    const maxNameLength = 1024 - jobIdAndExtLength - 10; // 10 bytes buffer

    if (maxNameLength > 0) {
      const truncatedName = truncateToByteLength(sanitizedName, maxNameLength);

      return `${truncatedName}-${jobId}${extension}`;
    }

    // If even jobId+extension is too long, just use jobId (shouldn't happen, but safety check)
    return `${jobId}${extension}`;
  }

  return objectName;
}

/**
 * Convert base64 string, File, or Blob to Blob
 */
async function toBlob(input: string | File | Blob): Promise<Blob> {
  // Type guard for Blob (File extends Blob, so check File first)
  if (typeof input !== 'string') {
    return input;
  }

  // Base64 string - convert to blob
  // Handle data URL format: data:image/png;base64,...
  let base64Data = input;

  if (input.startsWith('data:')) {
    const commaIndex = input.indexOf(',');

    if (commaIndex !== -1) {
      base64Data = input.substring(commaIndex + 1);
    }
  }

  // Decode base64 to binary
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Determine content type from base64 data URL if available
  let contentType = 'application/octet-stream';

  if (input.startsWith('data:')) {
    const mimeMatch = input.match(/data:([^;]+)/);

    if (mimeMatch) {
      contentType = mimeMatch[1];
    }
  }

  return new Blob([bytes], { type: contentType });
}

/**
 * Sanitize a path segment (e.g. user email) so it is safe for use in GCS object paths
 * Mirrors logic used in `gcsClientUpload`.
 */
function sanitizePathSegment(raw: string | undefined): string {
  if (!raw) {
    return 'anonymous';
  }

  return raw.replace(/[^a-zA-Z0-9-_]/g, '_').trim() || 'anonymous';
}

/**
 * Main asset upload function
 * Handles upload, polling for processing status, and returns final result
 */
export async function uploadAssetToGCS(
  file: string | File | Blob,
  options: AssetUploadOptions,
  signal?: AbortSignal,
): Promise<AssetUploadResult> {
  const { assetType, source, jobIdParam, projectId, metadata, onStatus, onProgress } = options;

  const userId = userIdStore.get();

  console.log('[uploadAssetToGCS] userId from store:', userId ? userId : 'empty');

  const baseObjectName = generateObjectName(jobIdParam, assetType, file instanceof File ? file.name : undefined);
  const objectName = `${userId}/${baseObjectName}`;
  const bucketName = getBucketForAssetType(assetType);

  // Add Sentry breadcrumb for upload start
  Sentry.addBreadcrumb({
    category: 'asset-upload',
    message: 'Asset upload started',
    data: {
      jobId: jobIdParam,
      assetType,
      source,
      bucketName,
      objectName,
    },
    level: 'info',
  });

  try {
    onStatus?.('Preparing file for upload...');

    // Convert to blob
    const blob = await toBlob(file);

    // Validate file size
    const maxSize =
      assetType === 'image' ? ASSET_UPLOAD_CONFIG.MAX_FILE_SIZE_IMAGE : ASSET_UPLOAD_CONFIG.MAX_FILE_SIZE_VIDEO;

    if (blob.size > maxSize) {
      const error = new Error(`File size exceeds maximum allowed size (${maxSize / 1024 / 1024}MB)`);
      Sentry.captureException(error, {
        tags: {
          action: 'asset_upload_validation',
          assetType,
        },
        extra: {
          jobId: jobIdParam,
          fileSize: blob.size,
          maxSize,
          source,
        },
      });
      throw error;
    }

    // Determine content type
    const contentType = blob.type || (assetType === 'image' ? 'image/png' : 'video/mp4');

    onStatus?.('Requesting upload session...');

    // Start resumable session using the existing GCS resumable API route
    const sessionParams = {
      objectName,
      contentType,
      contentLength: blob.size,
      bucketName, // Pass bucketName directly to the API route
    };

    const uploadUrl = await startResumableSession(sessionParams, '/api/gcs-resumable', signal);

    Sentry.addBreadcrumb({
      category: 'asset-upload',
      message: 'Upload session started',
      data: {
        jobId: jobIdParam,
        contentType,
        fileSize: blob.size,
      },
      level: 'info',
    });

    // Record upload in database before polling starts
    try {
      // Try to get token from localStorage or metadata, fallback to letting server get from cookies
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const recordResponse = await fetch('/api/asset-record', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          jobIdParam,
          projectId,
          assetType,
          source,
          originalObjectName: objectName,
          originalSize: blob.size,
        }),
        signal,
      });

      if (!recordResponse.ok) {
        const errorText = await recordResponse.text();
        console.warn('[assetClientUpload] Failed to record upload in database:', errorText);
        Sentry.captureException(new Error('Failed to record upload in database'), {
          tags: {
            action: 'asset_upload_record',
            assetType,
          },
          extra: {
            jobId: jobIdParam,
            status: recordResponse.status,
            error: errorText,
          },
          level: 'warning',
        });

        // Continue with polling even if recording fails
      } else {
        console.log('[assetClientUpload] Upload recorded in database:', jobIdParam);

        // Track jobId for later projectId update
        addPendingAssetJobId(jobIdParam);

        Sentry.addBreadcrumb({
          category: 'asset-upload',
          message: 'Upload recorded in database',
          data: { jobId: jobIdParam },
          level: 'info',
        });
      }
    } catch (recordError) {
      console.warn('[assetClientUpload] Error recording upload in database:', recordError);
      Sentry.captureException(recordError instanceof Error ? recordError : new Error(String(recordError)), {
        tags: {
          action: 'asset_upload_record',
          assetType,
        },
        extra: {
          jobId: jobIdParam,
        },
        level: 'warning',
      });

      // Continue with polling even if recording fails
    }

    onStatus?.('Uploading asset...');

    // Track progress
    let lastProgress = 0;

    const progressCallback = (current: number, total: number) => {
      const progress = Math.round((current / total) * 100);

      if (progress !== lastProgress) {
        onProgress?.(progress);
        lastProgress = progress;
      }
    };

    // Upload with resumable logic
    await uploadBlobResumable(uploadUrl, blob, {
      chunkSize: ASSET_UPLOAD_CONFIG.DEFAULT_CHUNK_SIZE,
      maxRetries: ASSET_UPLOAD_CONFIG.DEFAULT_RETRIES,
      onStatus: (message) => {
        onStatus?.(message);

        // Extract progress from message if possible
        const progressMatch = message.match(/(\d+)%/);

        if (progressMatch) {
          progressCallback(Number(progressMatch[1]), 100);
        } else {
          // Try to extract from byte count
          const byteMatch = message.match(/Uploaded (\d+) of (\d+) bytes/);

          if (byteMatch) {
            const current = Number(byteMatch[1]);
            const total = Number(byteMatch[2]);

            progressCallback(current, total);
          }
        }
      },
      contentType,
      signal,
    });

    Sentry.addBreadcrumb({
      category: 'asset-upload',
      message: 'File upload completed',
      data: {
        jobId: jobIdParam,
        fileSize: blob.size,
      },
      level: 'info',
    });

    onStatus?.('Upload complete. Recording upload in database...');

    onStatus?.('Waiting for processing...');

    // Start polling for processing status
    // Poll until processing is complete or failed
    const maxPollAttempts = 600; // 10 minutes max (1 second intervals)
    const pollInterval = 1000; // 1 second
    let pollAttempts = 0;

    while (pollAttempts < maxPollAttempts) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      pollAttempts++;

      try {
        const statusResponse = await fetch(`/api/asset-status?jobId=${encodeURIComponent(jobIdParam)}`, { signal });

        if (!statusResponse.ok) {
          // If status endpoint not found or error, continue polling
          onStatus?.(`Checking processing status... (attempt ${pollAttempts}/${maxPollAttempts})`);
          continue;
        }

        const statusData = (await statusResponse.json()) as AssetStatusResponse;

        console.log('[assetClientUpload] Status data:', statusData);

        switch (statusData.status) {
          case 'UPLOAD_STARTED': {
            // Upload has been registered in DB but not yet processed
            onStatus?.('Upload started...');
            break;
          }

          case 'PROCESSING_STARTED': {
            // Asset is being processed by backend
            onStatus?.('Processing in progress...');
            break;
          }

          case 'PROCESSING_COMPLETED': {
            onStatus?.('Processing complete!');

            // Log successful upload completion
            Sentry.addBreadcrumb({
              category: 'asset-upload',
              message: 'Asset upload and processing completed successfully',
              data: {
                jobId: jobIdParam,
                assetType,
                finalUrl: statusData.finalUrl,
              },
              level: 'info',
            });
            return {
              jobIdParam,
              status: 'completed',
              finalUrl: statusData.finalUrl,
            };
          }

          case 'UPLOAD_FAILED': {
            onStatus?.('Upload failed.');

            const errorMessage = statusData.error || 'Upload failed';
            Sentry.captureException(new Error(`Asset upload failed: ${errorMessage}`), {
              tags: {
                action: 'asset_upload_failed',
                assetType,
                failureStage: 'upload',
              },
              extra: {
                jobId: jobIdParam,
                source,
                objectName,
                error: errorMessage,
              },
            });
            return {
              jobIdParam,
              status: 'failed',
              error: errorMessage,
            };
          }

          case 'PROCESSING_FAILED': {
            onStatus?.('Processing failed.');

            const errorMessage = statusData.error || 'Processing failed';
            Sentry.captureException(new Error(`Asset processing failed: ${errorMessage}`), {
              tags: {
                action: 'asset_upload_failed',
                assetType,
                failureStage: 'processing',
              },
              extra: {
                jobId: jobIdParam,
                source,
                objectName,
                error: errorMessage,
              },
            });
            return {
              jobIdParam,
              status: 'failed',
              error: errorMessage,
            };
          }

          default: {
            onStatus?.(`Checking processing status... (attempt ${pollAttempts}/${maxPollAttempts})`);
          }
        }
      } catch (pollError) {
        if (pollError instanceof Error && pollError.name === 'AbortError') {
          Sentry.addBreadcrumb({
            category: 'asset-upload',
            message: 'Asset upload cancelled by user',
            data: { jobId: jobIdParam },
            level: 'info',
          });
          return {
            jobIdParam,
            status: 'failed',
            error: 'Extraction cancelled',
          };
        }

        // Continue polling on error
        console.warn('[assetClientUpload] Error polling status:', pollError);
        Sentry.captureException(pollError instanceof Error ? pollError : new Error(String(pollError)), {
          tags: {
            action: 'asset_upload_poll_error',
            assetType,
          },
          extra: {
            jobId: jobIdParam,
            pollAttempt: pollAttempts,
          },
          level: 'warning',
        });
      }
    }

    // Timeout - processing took too long
    const timeoutError = new Error('Processing timeout - asset processing took longer than expected');
    Sentry.captureException(timeoutError, {
      tags: {
        action: 'asset_upload_failed',
        assetType,
        failureStage: 'timeout',
      },
      extra: {
        jobId: jobIdParam,
        source,
        objectName,
        pollAttempts,
        maxPollAttempts,
      },
    });
    return {
      jobIdParam,
      status: 'failed',
      error: 'Processing timeout - asset processing took longer than expected',
    };
  } catch (error) {
    console.error('[assetClientUpload] Upload failed:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        action: 'asset_upload_failed',
        assetType,
        failureStage: 'exception',
      },
      extra: {
        jobId: jobIdParam,
        source,
        objectName,
        bucketName,
      },
    });
    return {
      jobIdParam,
      status: 'failed',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Update projectId for asset uploads after AI stream completes
 * @param jobIds - Array of jobIds to update
 * @param projectId - The projectId to associate
 * @returns Promise with update result
 */

export async function updateAssetUploadProjectId(
  jobIds: string[],
  projectId: string,
): Promise<{ success: boolean; updatedCount: number; error?: string }> {
  try {
    const response = await fetch('/api/asset-upload-update-projectid', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jobIds,
        projectId,
      }),
    });

    if (!response.ok) {
      const errorData = (await response.json()) as { error: string };
      throw new Error(errorData.error || 'Failed to update projectId');
    }

    const result = (await response.json()) as { success: boolean; updatedCount: number; error?: string };
    return result;
  } catch (error) {
    console.error('[updateAssetUploadProjectId] Error:', error);
    return {
      success: false,
      updatedCount: 0,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
